import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, SelectField, TextField, showToast } from 'components/ui';
import { PICKER_ETHNICITY } from 'constants/picker';
import updateProfile from 'features/profile/api/updateProfile';
import useSchoolList from 'features/schools/hooks/useSchoolList';
import serverErrorHandler from 'services/serverError.service';
import useUserStore from 'store/useUserStore';
import type { UserSession } from 'types';
import { phoneNumberRegex } from 'utils/regex';
import { applicantSchoolName } from '../utils/applicantProfileGate';
import styles from './CompleteApplicantInfo.module.scss';

const GRADE_OPTIONS = [
  ...['6', '7', '8', '9', '10', '11', '12'].map(value => ({
    label: `${value}th Grade`,
    value,
  })),
  { label: 'College Freshman', value: 'freshman' },
  { label: 'College Sophomore', value: 'sophomore' },
  { label: 'College Junior', value: 'junior' },
  { label: 'College Senior', value: 'senior' },
  { label: 'Graduated', value: 'graduated' },
  { label: 'Not in school', value: 'Not in school' },
  { label: 'Professional', value: 'professional' },
];

const ETHNICITY_OPTIONS = PICKER_ETHNICITY.map(option => ({
  label: option.label,
  value: option.value,
}));

interface ICompleteApplicantInfoProps {
  user: UserSession;
}

function formatPhoneInput(raw: string): string {
  const digits = raw.replace(/\D/g, '').slice(0, 10);
  if (digits.length < 4) return digits;
  if (digits.length < 7) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

export default function CompleteApplicantInfo({ user }: ICompleteApplicantInfoProps) {
  const setUser = useUserStore(s => s.setUser);
  const { schoolOptions, knownSchoolValues, loading: schoolsLoading } = useSchoolList();
  const [phoneNumber, setPhoneNumber] = useState(user.phoneNumber || '');
  const [school, setSchool] = useState(applicantSchoolName(user));
  const [schoolOther, setSchoolOther] = useState('');
  const [grade, setGrade] = useState(user.gradeLevel || '');
  const [ethnicity, setEthnicity] = useState(user.ethnicity || '');
  const [ethnicityOther, setEthnicityOther] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  const schoolChoices = useMemo(
    () => [...schoolOptions, { label: 'Other', value: 'other' }],
    [schoolOptions],
  );

  useEffect(() => {
    if (schoolsLoading || !school || school === 'other') return;
    const match = schoolOptions.find(
      option => option.value.toLocaleLowerCase() === school.toLocaleLowerCase(),
    );
    if (match) {
      if (match.value !== school) setSchool(match.value);
      return;
    }
    if (knownSchoolValues.size === 0) return;
    setSchoolOther(school);
    setSchool('other');
  }, [schoolsLoading, knownSchoolValues, schoolOptions, school]);

  const onPhoneChange = useCallback((value: string) => {
    setPhoneNumber(formatPhoneInput(value));
  }, []);
  const onSchoolChange = useCallback((value: string | null) => {
    setSchool(value ?? '');
  }, []);
  const onSchoolOtherChange = useCallback((value: string) => {
    setSchoolOther(value);
  }, []);
  const onGradeChange = useCallback((value: string | null) => {
    setGrade(value ?? '');
  }, []);
  const onEthnicityChange = useCallback((value: string | null) => {
    setEthnicity(value ?? '');
  }, []);
  const onEthnicityOtherChange = useCallback((value: string) => {
    setEthnicityOther(value);
  }, []);

  const preventSubmit = useCallback((event: React.FormEvent) => {
    event.preventDefault();
  }, []);

  const onSave = useCallback(async () => {
    const nextErrors: Record<string, string> = {};
    if (!phoneNumberRegex.test(phoneNumber)) {
      nextErrors.phoneNumber = 'Enter a 10-digit phone number.';
    }
    const schoolName = school === 'other' ? schoolOther.trim() : school.trim();
    if (!schoolName) nextErrors.school = 'Select your school.';
    if (!grade) nextErrors.grade = 'Select your grade level.';
    const ethnicityValue = ethnicity === 'other' ? ethnicityOther.trim() : ethnicity.trim();
    if (!ethnicityValue) nextErrors.ethnicity = 'Select your ethnicity.';
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    try {
      setSaving(true);
      const updated = await updateProfile({
        phoneNumber,
        schoolName,
        gradeLevel: grade,
        ethnicity: ethnicityValue,
      });
      setUser(updated);
      showToast({ title: 'Info saved' });
    } catch (error) {
      serverErrorHandler(error);
    } finally {
      setSaving(false);
    }
  }, [
    phoneNumber,
    school,
    schoolOther,
    grade,
    ethnicity,
    ethnicityOther,
    setUser,
  ]);

  return (
    <section className={styles.page}>
      <h1 className={styles.title}>Complete your info</h1>
      <p className={styles.copy}>
        The SCLA application needs your phone number, school, grade, and ethnicity.
        This is saved to your profile.
      </p>
      <form className={styles.form} onSubmit={preventSubmit}>
        <TextField
          id="applicant-phone"
          label="Phone number"
          value={phoneNumber}
          onChange={onPhoneChange}
          error={errors.phoneNumber}
          placeholder="(213) 555-0100"
          type="tel"
          autoComplete="tel"
        />
        <SelectField
          id="applicant-school"
          label="School"
          value={school || null}
          onChange={onSchoolChange}
          options={schoolChoices}
          error={errors.school}
          searchable
          placeholder="Search schools"
        />
        {school === 'other' ? (
          <TextField
            id="applicant-school-other"
            label="School name"
            value={schoolOther}
            onChange={onSchoolOtherChange}
            error={errors.school}
          />
        ) : null}
        <SelectField
          id="applicant-grade"
          label="Grade level"
          value={grade || null}
          onChange={onGradeChange}
          options={GRADE_OPTIONS}
          error={errors.grade}
        />
        <SelectField
          id="applicant-ethnicity"
          label="Ethnicity"
          value={ethnicity || null}
          onChange={onEthnicityChange}
          options={ETHNICITY_OPTIONS}
          error={errors.ethnicity}
        />
        {ethnicity === 'other' ? (
          <TextField
            id="applicant-ethnicity-other"
            label="Enter your ethnicity"
            value={ethnicityOther}
            onChange={onEthnicityOtherChange}
            error={errors.ethnicity}
          />
        ) : null}
        <Button type="button" block loading={saving} onClick={onSave}>
          Continue
        </Button>
      </form>
    </section>
  );
}
